import { useState, useMemo } from 'react'
import { useRouter } from 'next/router'
import qs from 'qs'
import { useQuery } from 'react-query'

import { useShopify } from '~context'
import { getShopifySdk } from '~lib/shopify/client'
import { ProductTagsQuery } from '~lib/shopify/sdk'

export const useFilters = (filtersMapping: ProductTagsQuery['productTags']) => {
  const router = useRouter()
  const { portion } = useShopify()
  const { push, query, locale } = router

  const [isShowingFilters, setIsShowingFilters] = useState(false)
  const changeTagsToQuery = (filters: { [index: string]: string[] }) => {
    const tags = (Object.entries(filters) || []).map(([prefix, items]) => {
      if (typeof items === 'string') {
        return [`${prefix}_${items}`]
      }

      const stringQuery = items
        ?.map(e => `"${prefix}_${e}"`)
        .join(prefix === 'spiciness' ? ' OR ' : ' ')

      return stringQuery
    })

    return `${tags.join(' ')} portion_${
      portion || 'normal'
    } available_for_sale:true`
  }

  const parsedQuery = useMemo(
    () =>
      (qs.parse(query?.tags as string) ?? []) as {
        [index: string]: string[]
      },
    [query],
  )

  const fetchProducts = async () => {
    const data = await getShopifySdk(locale).products({
      first: 250,
      query: `${changeTagsToQuery(parsedQuery)}`,
    })

    return data.products
  }

  const {
    data: products,
    isLoading,
    error,
  } = useQuery(['products', query], fetchProducts)

  const handleFilterChange = (filters: { [index: string]: string[] }) => {
    push(
      {
        query: {
          ...query,
          tags: qs.stringify(filters),
          ...(Object.keys(filters)?.length
            ? { tags: qs.stringify(filters) }
            : {}),
        },
      },
      undefined,
      {
        shallow: true,
      },
    )
  }

  const handleReset = () =>
    push({ query: { ...query, tags: undefined } }, undefined, {
      shallow: true,
    })

  return {
    isShowingFilters,
    setIsShowingFilters,
    changeTagsToQuery,
    parsedQuery,
    handleFilterChange,
    handleReset,
    products,
    isLoading,
    error,
  }
}
